<template>
    <form-validation v-slot="{ bindings: validationBindings }">
        <modal-dialog
            ref="duplicateLeadModal"
            label="Lead toevoegen aan andere pijplijn"
            description="Lead toevoegen aan andere pijplijn"
            :size="4"
        >
            <flex-container>
                <flex-container direction="column" :gap="Size.sm">
                    <div>
                        <text-heading>
                            <span>Lead toevoegen aan andere pijplijn</span>
                        </text-heading>
                    </div>

                    <text-description>
                        Het is mogelijk om snel nog een extra lead type te registreren voor deze
                        contactpersoon. Hieronder kun je het lead type instellen en aan welk team
                        deze wordt toegewezen.
                    </text-description>
                </flex-container>

                <flex-container>
                    <flex-container :size="5">
                        <input-group
                            v-model="formData.pipeline"
                            :schema="formSchema.pipeline"
                        >
                            <template #label>
                                <input-label>
                                    Pijplijn
                                </input-label>
                            </template>
                            <template #input>
                                <pipeline-select-input
                                    :pipelines="availablePipelines"
                                />
                            </template>
                        </input-group>
                    </flex-container>
                    <flex-container :size="5">
                        <input-group
                            v-model="formData.source"
                            :schema="formSchema.source"
                        >
                            <template #label>
                                <input-label>
                                    Bron
                                </input-label>
                            </template>
                            <template #input>
                                <select-input
                                    :items="availableSources"
                                    value-key-name="id"
                                    label-key-name="name"
                                />
                            </template>
                        </input-group>
                    </flex-container>
                </flex-container>

                <flex-container>
                    <flex-container :size="5">
                        <input-group
                            v-model="formData.phase"
                            :schema="formSchema.phase"
                        >
                            <template #label>
                                <input-label>
                                    Fase
                                </input-label>
                            </template>
                            <template #input>
                                <select-input
                                    :items="availablePhases"
                                    value-key-name="id"
                                    label-key-name="name"
                                    :disabled="!formData.pipeline"
                                />
                            </template>
                        </input-group>
                    </flex-container>

                    <flex-container :size="5">
                        <input-group
                            v-model="formData.referredByTeam"
                            :schema="formSchema.referredByTeam"
                        >
                            <template #label>
                                <input-label>
                                    Aangemaakt door team
                                </input-label>
                            </template>
                            <template #input>
                                <team-select-input
                                    :teams="availableReferredByTeams as Team[]"
                                    :disabled="!formData.phase"
                                />
                            </template>
                        </input-group>
                    </flex-container>
                </flex-container>

                <flex-container>
                    <flex-container :size="5">
                        <input-group
                            v-model="formData.assignedToTeam"
                            :schema="formSchema.assignedToTeam"
                        >
                            <template #label>
                                <input-label>
                                    Toegewezen aan team
                                </input-label>
                            </template>
                            <template #input>
                                <team-select-input
                                    :teams="availableAssignedToTeams as Team[]"
                                    :disabled="!formData.pipeline"
                                />
                            </template>
                        </input-group>
                    </flex-container>

                    <flex-container :size="5">
                        <input-group
                            v-model="formData.assignedToUser"
                            :schema="formSchema.assignedToUser"
                        >
                            <template #label>
                                <input-label>
                                    Toegewezen aan gebruiker (optioneel)
                                </input-label>
                            </template>
                            <template #input>
                                <user-select-input
                                    :users="availableAssignedToUsers"
                                    :disabled="!formData.assignedToTeam"
                                />
                            </template>
                        </input-group>
                    </flex-container>
                </flex-container>

                <flex-container :gap="Size.xs" justify-content="flex-end">
                    <input-group
                        v-model="formData.comment"
                        :schema="formSchema.comment"
                    >
                        <template #label>
                            <input-label>
                                Notitie (optioneel)
                            </input-label>
                        </template>
                        <template #input>
                            <rich-text-area-input :mentionables="mentionables" />
                        </template>
                    </input-group>
                    <input-group
                        v-model="formData.commentVisibility"
                        :schema="formSchema.commentVisibility"
                        inline
                    >
                        <template #input>
                            <comment-visibility-input />
                        </template>
                    </input-group>
                </flex-container>
            </flex-container>

            <template #footer>
                <dialog-footer>
                    <button-outline @click="redirectToDetail">
                        Annuleren
                    </button-outline>

                    <button-block-confirm :disabled="!validationBindings.isValid.value" @confirmed="onSave($event)">
                        Toevoegen

                        <template #confirmation>
                            Weet je het zeker?
                        </template>
                    </button-block-confirm>
                </dialog-footer>
            </template>
        </modal-dialog>
    </form-validation>
</template>

<script setup lang="ts">
    import {
        ModalDialog,
        ButtonOutline,
        TextDescription,
        type ExposedOverlay,
        ButtonBlockConfirm,
        FlexContainer,
        InputGroup,
        InputLabel,
        SelectInput,
        DialogFooter,
        Size,
    } from '@keyvue/one-ui';

    import {
        CommentVisibility,
        Pipeline,
        PhaseId,
        TeamId,
        UserId,
        Source,
        SourceId,
        Channel,
        type TeamSubResource,
        User,
        Team,
        type CompanySubResource,
        Phase,
        PipelineId,
    } from '@keyvue/one-api-client';

    import {
        computed,
        type Ref,
        ref,
        watch,
    } from 'vue';
    import { useRouter } from 'vue-router';
    import useClient from '@composables/useClient';
    import useMentionable from '@composables/useMentionable';
    import {
        Comment as LegacyComment,
        CommentAPI,
        CommentOrigin as LegacyCommentOrigin,
        CommentVisibility as LegacyCommentVisibility,
        createResourceIdentifier,
        ReferralAPI,
    } from '@keyvue/one-api-client-legacy';
    import { v4 as uuid } from 'uuid';
    import {
        object,
        string,
        StringSchema,
    } from 'yup';
    import CommentVisibilityInput from '@components/Inputs/CommentVisibilityInput.vue';
    import UserSelectInput from '@components/Inputs/UserSelectInput.vue';
    import TeamSelectInput from '@components/Inputs/TeamSelectInput.vue';
    import PipelineSelectInput from '@components/Inputs/PipelineSelectInput.vue';
    import useOverlayAsRoute from '@composables/useOverlayAsRoute';

    interface Props {
        contactId: string;
        leadId: string;
    }

    interface FormData {
        pipeline: Pipeline | null;
        source: Source | null;
        phase: Phase | null;
        referredByTeam: Team | null;
        assignedToTeam: Team | null;
        assignedToUser: User | null;
        comment: string;
        commentVisibility: { value: CommentVisibility, label: string, };
    }

    const { client, getReactiveCollection, currentCompany, currentUser } = useClient();

    if (!currentCompany.value || !currentUser.value) {
        throw new Error('Not authenticated');
    }

    const router = useRouter();
    const duplicateLeadModal = ref<ExposedOverlay>();
    const props = defineProps<Props>();
    const formData = ref<FormData>({
        pipeline: null,
        source: null,
        phase: null,
        referredByTeam: null,
        assignedToTeam: null,
        assignedToUser: null,
        comment: '',
        commentVisibility: CommentVisibility.options[0],
    }) as Ref<FormData>;

    /** aliases are because something is being exported wrongly in one-api-client */
    const formSchema = {
        pipeline: object({
            id: PipelineId.validationSchema as unknown as StringSchema,
        }),
        source: object({
            id: SourceId.validationSchema as unknown as StringSchema,
        }),
        phase: object({
            id: PhaseId.validationSchema as unknown as StringSchema,
        }),
        referredByTeam: object({
            id: TeamId.validationSchema as unknown as StringSchema,
        }),
        assignedToTeam: object({
            id: TeamId.validationSchema as unknown as StringSchema,
        }),
        assignedToUser: object({
            id: UserId.validationSchema as unknown as StringSchema,
        }).nullable(),
        comment: string().notRequired(),
        commentVisibility: object(),
    };

    const userTeams = await client.getSubResources(currentUser.value, Team, 'teams');
    const availableAssignedToUsers = ref<User[]>([]) as Ref<User[]>;
    const mentionable = useMentionable(availableAssignedToUsers);
    const mentionables = computed(() => mentionable.mentionables.value);
    const sourceCollection = await getReactiveCollection(Source, {
        active: true,
        channel: Channel.USER,
        company: currentCompany.value.id,
    });
    const pipelines = await client.getSubResources<CompanySubResource, Pipeline>(
        currentCompany.value,
        Pipeline,
        'available-pipelines',
    );

    const createLegacyLead = async () => {
        if (!client.session.userId) {
            throw new Error('Niet ingelogd');
        }

        if (!formData.value?.pipeline?.id) {
            throw new Error('Geen pijplijn gekozen');
        }

        const referralId = uuid();
        const requestData = ref<{ [key: string]: string | boolean | number }>({
            contact: createResourceIdentifier('contacts', props.contactId),
            referredBy: createResourceIdentifier('users', client.session.userId.value),
            id: referralId,
            status: 'open',
            notifyBranchManagers: false,
            pipeline: createResourceIdentifier('pipelines', formData.value.pipeline.id.toString()),
        });

        if (!formData.value?.source) {
            throw new Error('Geen bron gekozen');
        }

        if (!formData.value?.phase) {
            throw new Error('Geen fase gekozen');
        }

        if (!formData.value?.referredByTeam) {
            throw new Error('Geen doorverwezen door team gekozen');
        }

        if (!formData.value?.assignedToTeam) {
            throw new Error('Geen toegewezen aan team gekozen');
        }

        if (formData.value.source) {
            requestData.value.newSource = createResourceIdentifier(
                'sources',
                formData.value.source.id.value,
            );
        }

        if (formData.value.phase) {
            requestData.value.phase = createResourceIdentifier(
                'pipeline_phases',
                formData.value.phase.id.value,
            );
        }

        if (formData.value.referredByTeam) {
            requestData.value.referredByBranch = createResourceIdentifier(
                'branches',
                formData.value.referredByTeam.id.value,
            );
        }

        if (formData.value.assignedToTeam) {
            requestData.value.assignedToBranch = createResourceIdentifier(
                'branches',
                formData.value.assignedToTeam.id.value,
            );
        }

        if (formData.value.assignedToUser) {
            requestData.value.assignedTo = createResourceIdentifier(
                'users',
                formData.value.assignedToUser.id.value,
            );
        }

        await ReferralAPI.create(requestData.value as any);

        if (!formData.value.comment) {
            return;
        }

        const newLegacyComment = new LegacyComment(
            createResourceIdentifier('referrals', referralId),
            createResourceIdentifier('users', client.session.userId.value),
            uuid(),
            formData.value.comment,
            LegacyCommentOrigin.USER,
            formData.value.commentVisibility.value.value as LegacyCommentVisibility,
            new Date(),
            new Date(),
        );
        await CommentAPI.create(newLegacyComment);
        await mentionable.mention(newLegacyComment);
    };

    const onSave = async (event: any) => {
        await createLegacyLead();
        event.button.setState('completed');
        redirectToDetail();
    };

    const availablePipelines = computed(
        () => pipelines.data,
    );

    const availableSources = computed(
        () => (
            [...sourceCollection.data.value]
                .sort((a, b) => a.name.localeCompare(b.name))
        ),
    );

    const availablePhases = computed(
        () => (
            [...(formData.value.pipeline?.phases ?? [])]
                .filter((phase) => phase.active)
                .sort((a, b) => a.compareTo(b))
        ),
    );

    const availableReferredByTeams = computed(
        () => (
            [...userTeams.data]
                .filter((team) => team.active)
                .sort((a, b) => a.name.localeCompare(b.name))
        ),
    );

    const availableAssignedToTeams = computed(
        () => (
            [...(formData.value.pipeline?.teams ?? [])]
                .filter((team) => team.active)
                .sort((a, b) => a.name.localeCompare(b.name))
        ),
    );

    const redirectToDetail = () => {
        router.push({
            name: 'contactLeadDetail',
            params: {
                contactId: props.contactId,
                leadId: props.leadId,
            },
        });
    };

    useOverlayAsRoute({
        overlay: duplicateLeadModal,
        isSideSheet: false,
        redirectMethod: redirectToDetail,
    });

    watch(formData, async () => {
        if (!formData.value.assignedToTeam) {
            return;
        }

        const userCollection = await client.getSubResources<TeamSubResource, User>(
            formData.value.assignedToTeam as Team,
            User,
            'available-users',
        );

        availableAssignedToUsers.value = userCollection.data;
    }, { deep: true });
</script>
